import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { i18nNS } from '../../../i18n';
import { getAlphabetFromIndex } from '../../../utils/helper';
import usePollTitleForm from './Header/PollTitleForm/vm';
import usePollQuestionForm from './PollQuestionForm/vm';

const usePollPage = () => {
  const { t } = useTranslation([i18nNS.POLL]);

  const [shouldUseRequiredValidator, setShouldUseRequiredValidator] = useState(false);

  const {
    formMethods: pollTitleFormMethods,
    onSubmit: onSubmitPollTitle,
    onLocalSubmit: onLocalSubmitPollTitle,
  } = usePollTitleForm({
    shouldUseRequiredValidator,
  });
  const {
    formState: { errors: pollTitleFormErrors },
  } = pollTitleFormMethods;

  const {
    formMethods: pollQuestionFormMethods,
    onSubmit: onSubmitPollQuestion,
    onLocalSubmit: onLocalSubmitPollQuestion,
  } = usePollQuestionForm({
    shouldUseRequiredValidator,
  });
  const {
    formState: { errors: pollQuestionFormErrors },
  } = pollQuestionFormMethods;

  const errors = {
    [t('poll_title', { ns: i18nNS.POLL })]: pollTitleFormErrors.title?.message,
    [t('question_description', { ns: i18nNS.POLL })]: pollQuestionFormErrors.description?.message,
    [t('question_attachments', { ns: i18nNS.POLL })]: pollQuestionFormErrors.attachments?.flatMap?.(
      (attachmentError) => {
        if (!attachmentError) return [];
        if (!attachmentError.message) return [];
        return [attachmentError.message];
      }
    ),
    [t('question_options', { ns: i18nNS.POLL })]: Object.keys(pollQuestionFormErrors.options ?? {}).reduce(
      (acc, optionErrorKey) => {
        if (optionErrorKey === 'root') return acc;
        const value = pollQuestionFormErrors.options?.[+optionErrorKey];
        const errorMessage = value?.text?.message;
        if (!errorMessage) return acc;
        const [_, fieldIndex] = value?.text?.ref?.name.split('.') ?? [];
        if (!fieldIndex) return acc;
        acc[getAlphabetFromIndex(+fieldIndex)] = errorMessage;
        return acc;
      },
      {} as Record<string, string>
    ),
  };

  const handleValidateForms = useCallback(async () => {
    setShouldUseRequiredValidator(true);

    // we have to wait for set state to finish its work, so both forms get time to set their validation schema
    await new Promise((resolve) => setTimeout(resolve, 0));

    const isPollTitleFormValid = await pollTitleFormMethods.trigger(undefined, { shouldFocus: true });
    const isPollQuestionFormValid = await pollQuestionFormMethods.trigger(undefined, { shouldFocus: true });

    setShouldUseRequiredValidator(false);

    return isPollTitleFormValid && isPollQuestionFormValid;
  }, [pollTitleFormMethods.trigger, pollQuestionFormMethods.trigger]);

  return {
    pollTitleFormMethods,
    onSubmitPollTitle,
    onLocalSubmitPollTitle,
    pollQuestionFormMethods,
    onSubmitPollQuestion,
    onLocalSubmitPollQuestion,
    errors,
    handleValidateForms,
  };
};

export default usePollPage;
